import { useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { SUPABASE_URL } from '../lib/constants';
import { ClarifyQuestion } from '../components/ClarifyQuestionsForm';

export interface WorkflowData {
  triage: {
    status: string;
    started_at: string | null;
    completed_at: string | null;
    human_intervention: boolean;
    notes: string;
    substeps: Array<{ id: string; title: string; completed: boolean }>;
    awaiting_confirmation: boolean;
  };
  clarify: {
    status: string;
    started_at: string | null;
    completed_at: string | null;
    human_intervention: boolean;
    notes: string;
    substeps: Array<{ id: string; title: string; completed: boolean }>;
    awaiting_confirmation: boolean;
    clarifying_questions?: ClarifyQuestion[];
  };
  plan: {
    status: string;
    started_at: string | null;
    completed_at: string | null;
    human_intervention: boolean;
    notes: string;
    substeps: Array<{ id: string; title: string; completed: boolean }>;
    awaiting_confirmation: boolean;
  };
  outreach: {
    status: string;
    started_at: string | null;
    completed_at: string | null;
    human_intervention: boolean;
    notes: string;
    substeps: Array<{ id: string; title: string; completed: boolean }>;
    awaiting_confirmation: boolean;
  };
  analyze: {
    status: string;
    started_at: string | null;
    completed_at: string | null;
    human_intervention: boolean;
    notes: string;
    substeps: Array<{ id: string; title: string; completed: boolean }>;
    awaiting_confirmation: boolean;
  };
  schedule: {
    status: string;
    started_at: string | null;
    completed_at: string | null;
    human_intervention: boolean;
    notes: string;
    substeps: Array<{ id: string; title: string; completed: boolean }>;
    awaiting_confirmation: boolean;
  };
}

export interface WorkflowRecord {
  id: string;
  service_request_id: string;
  current_stage: string;
  workflow_data: WorkflowData;
  created_at: string;
  updated_at: string;
}

export interface TriageAnalysisResult {
  service_category: string;
  complexity: string;
  urgency_assessment: string;
  summary: string;
  clarifying_questions: string[];
  notes_for_team?: string;
}

export interface ClarifyAnswersResult {
  sufficient_information: boolean;
  summary: string;
  additional_questions: string[];
  readiness_assessment: string;
  new_title?: string;
}

export const useServiceRequestWorkflow = (serviceRequestId: string | null) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [workflow, setWorkflow] = useState<WorkflowRecord | null>(null);

  const fetchWorkflow = useCallback(async () => {
    if (!serviceRequestId) return;

    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchError } = await supabase
        .from('service_request_agent_workflow')
        .select('*')
        .eq('service_request_id', serviceRequestId)
        .single();

      if (fetchError) {
        if (fetchError.code === 'PGRST116') {
          // No workflow exists, initialize one
          await initializeWorkflow();
        } else {
          throw fetchError;
        }
      } else {
        setWorkflow(data);
      }
    } catch (err: any) {
      console.error('Error fetching workflow:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [serviceRequestId]);

  const initializeWorkflow = useCallback(async () => {
    if (!serviceRequestId) return;

    try {
      const initialWorkflowData: WorkflowData = {
        triage: {
          status: 'pending',
          started_at: null,
          completed_at: null,
          human_intervention: false,
          notes: '',
          substeps: [],
          awaiting_confirmation: false,
        },
        clarify: {
          status: 'pending',
          started_at: null,
          completed_at: null,
          human_intervention: false,
          notes: '',
          substeps: [],
          awaiting_confirmation: false,
        },
        plan: {
          status: 'pending',
          started_at: null,
          completed_at: null,
          human_intervention: false,
          notes: '',
          substeps: [],
          awaiting_confirmation: false,
        },
        outreach: {
          status: 'pending',
          started_at: null,
          completed_at: null,
          human_intervention: false,
          notes: '',
          substeps: [],
          awaiting_confirmation: false,
        },
        analyze: {
          status: 'pending',
          started_at: null,
          completed_at: null,
          human_intervention: false,
          notes: '',
          substeps: [],
          awaiting_confirmation: false,
        },
        schedule: {
          status: 'pending',
          started_at: null,
          completed_at: null,
          human_intervention: false,
          notes: '',
          substeps: [],
          awaiting_confirmation: false,
        },
      };

      const { data, error: insertError } = await supabase
        .from('service_request_agent_workflow')
        .insert({
          service_request_id: serviceRequestId,
          current_stage: 'triage',
          workflow_data: initialWorkflowData,
        })
        .select()
        .single();

      if (insertError) throw insertError;
      
      setWorkflow(data);
      return data;
    } catch (err: any) {
      console.error('Error initializing workflow:', err);
      setError(err.message);
      throw err;
    }
  }, [serviceRequestId]);

  const analyzeTriage = useCallback(async (): Promise<TriageAnalysisResult> => {
    if (!serviceRequestId) {
      throw new Error('Service request ID is required');
    }

    setLoading(true);
    setError(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      const response = await fetch(
        `${SUPABASE_URL}/functions/v1/analyze-service-request`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session?.access_token}`,
          },
          body: JSON.stringify({
            service_request_id: serviceRequestId,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to analyze service request');
      }

      const result = await response.json();
      
      // Refresh workflow to get updated data
      await fetchWorkflow();
      
      return result.analysis;
    } catch (err: any) {
      console.error('Error analyzing service request:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [serviceRequestId, fetchWorkflow]);

  const submitClarifyAnswers = useCallback(async (
    answers: ClarifyQuestion[]
  ): Promise<ClarifyAnswersResult> => {
    if (!serviceRequestId) {
      throw new Error('Service request ID is required');
    }

    setLoading(true);
    setError(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      const response = await fetch(
        `${SUPABASE_URL}/functions/v1/process-clarifying-answers`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session?.access_token}`,
          },
          body: JSON.stringify({
            service_request_id: serviceRequestId,
            questions_and_answers: answers.map(qa => ({
              question: qa.question,
              answer: qa.answer,
            })),
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to process answers');
      }

      const result = await response.json();
      
      // Refresh workflow to get updated data
      await fetchWorkflow();
      
      return {
        sufficient_information: result.sufficient_information,
        summary: result.summary,
        additional_questions: result.additional_questions || [],
        readiness_assessment: result.readiness_assessment,
        new_title: result.new_title,
      };
    } catch (err: any) {
      console.error('Error processing clarify answers:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [serviceRequestId, fetchWorkflow]);

  return {
    workflow,
    loading,
    error,
    fetchWorkflow,
    initializeWorkflow,
    analyzeTriage,
    submitClarifyAnswers,
  };
};
